"use client";

import { artifactLine, policyIdentifier, shortId } from "../onboarding-summary";
import type { OnboardingProgress } from "../onboarding-progress";
import type { StepKey } from "../onboarding-steps";
import type { ReactNode } from "react";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../onboarding.styles";

export function doneResultFor({
  key,
  progress,
  agentLabel,
}: Readonly<{
  key: StepKey;
  progress: OnboardingProgress;
  agentLabel: string;
}>): ReactNode {
  switch (key) {
    case "signIn":
      return progress.viewerName ? (
        <span {...stylex.props(styles.machine)}>{progress.viewerName}</span>
      ) : null;
    case "player": {
      return <CompletedPlayer player={progress.player} />;
    }
    case "handOff":
      return progress.agentName ? <span>{agentLabel}</span> : null;
    case "build":
      return <CompletedBuild progress={progress} />;
    case "request": {
      return <CompletedRequest request={progress.experienceRequest} />;
    }
    case "submit": {
      const submission = progress.leagueSubmission;

      if (!submission) return null;

      return (
        <>
          <span {...stylex.props(styles.machine)}>
            {shortId(submission.id)}
          </span>
          <span>{submission.status}</span>
        </>
      );
    }
  }
}

function CompletedRequest({
  request,
}: Readonly<{
  request: OnboardingProgress["experienceRequest"];
}>) {
  if (!request) return null;

  const running =
    request.status === "running" &&
    (request.episodes > 0
      ? `Running — ${request.completed} of ${request.episodes} episodes`
      : "Running…");
  return (
    <>
      <span {...stylex.props(styles.machine)}>{shortId(request.id)}</span>
      <span>{running || request.status}</span>
    </>
  );
}

function CompletedPlayer({
  player,
}: Readonly<{ player: OnboardingProgress["player"] }>) {
  if (!player) return null;

  return (
    <>
      {player.avatarUrl ? (
        <img src={player.avatarUrl} alt="" {...stylex.props(styles.avatar)} />
      ) : null}
      <span {...stylex.props(styles.machine)}>{player.name}</span>
    </>
  );
}

function CompletedBuild({
  progress,
}: Readonly<{ progress: OnboardingProgress }>) {
  if (!progress.policyName) return null;

  const artifact = artifactLine(progress);
  return (
    <>
      {progress.player ? <span>{progress.player.name} ·</span> : null}
      <span {...stylex.props(styles.machine)}>
        {policyIdentifier(progress)}
      </span>
      {artifact ? <span>{artifact}</span> : null}
    </>
  );
}
